import classnames from 'classnames';
import { action, observable } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';

import { TranslateFn, withI18n, WithI18nProps } from '../../../internationalization/i18n-hoc';
import { i18next } from '../../../internationalization/i18next';
import { PlotKeyToggleState } from '../../../stores/plot-key-toggle-state';
import { Plot, QuizResultsHelper } from '../../../stores/quiz-results-store';
import { assertNever } from '../../../util/types';
import { SVGBuilder, ViewBox } from '../svg-builder/svg-builder';

const styles = require('./plot-key-legend.css');

interface PlotKeyLabelProps {
  plotkey: keyof Plot;
  title: string;
  disabled?: boolean;

  plotKeyToggleState: PlotKeyToggleState;
}

/**
 * Renders the legend (also toggle button action)
 */
@observer
export class PlotKeyLabel extends React.Component<PlotKeyLabelProps> {
  @observable
  isShown(): boolean {
    return this.props.plotKeyToggleState.isShown(this.props.plotkey);
  }

  @observable
  getStyleForToggle(): string {
    if (this.isShown()) {
      return this.props.plotkey === 'actual'
        ? classnames(styles.toggle, styles.actual)
        : classnames(styles.toggle, styles.shown);
    }
    return styles.toggle;
  }

  getTrianglePoints() {
    return `${this.getPointCoordinateFromPercentage(0.4, 30)}`;
  }

  getPointCoordinateFromPercentage(percentage: number, total: number) {
    return percentage * total;
  }

  renderKeyIcon(): JSX.Element | undefined {
    const viewBox: ViewBox = {
      dimX: 30,
      dimY: 30,
      posX: 0,
      posY: 0
    };
    const strokeWidth: number = 1.5;

    if (this.props.plotkey === 'actual') {
      return undefined;
    } else if (this.props.plotkey === 'expected') {
      return new SVGBuilder(strokeWidth, viewBox)
        .addLine({ x1: 15, y1: 15, x2: 15, y2: 30, style: 'solid' })
        .addCircle({ x: 15, y: 15, r: 4, style: 'filled' })
        .build();
    } else if (this.props.plotkey === 'group') {
      return new SVGBuilder(strokeWidth, viewBox)
        .addLine({ x1: 15, y1: 15, x2: 0, y2: 15, style: 'dotted' })
        .addCircle({ x: 15, y: 15, r: 4, style: 'shaded' })
        .build();
    } else if (this.props.plotkey === 'previous') { 
      return new SVGBuilder(strokeWidth, viewBox)
        .addLine({ x1: 0, y1: 15, x2: 30, y2: 15, style: 'dashed', stroke: 'dark' })
        .build();
    } else if (this.props.plotkey === 'industry') {
      return new SVGBuilder(strokeWidth, viewBox)
        .addLine({ x1: 15, y1: 15, x2: 0, y2: 15, style: 'dashed' })
        .addTriangle({ x: 15, y: 15, size: 4 })
        .build();
    } else {
      return undefined;
    }
  }
  /** Renders the component */
  render() {
    return (
      <div className={styles.labelContainer} onClick={() => this.onClick()}>
        <div className={this.getStyleForToggle()}>{this.isShown() && this.renderKeyIcon()}</div>
        <div className={this.isShown() ? classnames(styles.label, styles.bold) : styles.label}>{this.props.title}</div>
      </div>
    );
  }

  @action onClick() {
    this.props.plotKeyToggleState.toggle(this.props.plotkey);
  }
}

export interface PlotKeyLegendProps extends WithI18nProps {
  qr: QuizResultsHelper;
  plotKeyToggleState: PlotKeyToggleState;
  displayHeadlineRatio: boolean;
  displayHeadlineText?: boolean;
}

function plotToLegendName(
  qr: QuizResultsHelper,
  t: TranslateFn,
  plotkey: keyof Plot,
  displayHeadline: boolean
): string {
  const actualLabel = qr.getActualLabel(t);
  const expectedLabel = displayHeadline
    ? qr.getExpectedLabel(t)
    : qr.isRoleSelected()
      ? qr.getExpectedLabel(t)
      : t(qr.quiz.workRole.shortname);
  const groupLabel = qr.getGroupLabel(t);
  const industryLabel = qr.getIndustryLabel(t);
  switch (plotkey) {
    case 'actual':
      return actualLabel !== undefined ? actualLabel : i18next.t('legend.current', 'Current Results');
    case 'expected':
      return expectedLabel !== undefined ? expectedLabel : i18next.t('legend.desired', 'Desired');
    case 'difference':
      return i18next.t('legend.difference', 'Compared to Desired');
    case 'previous':
      return i18next.t('legend.previous', 'Previous Results');
    case 'group':
      return groupLabel !== undefined ? groupLabel : i18next.t('legend.group', 'Group Average');
    case 'industry':
      return industryLabel !== undefined ? industryLabel : i18next.t('legend.industry', 'Industry Average');
    default:
      assertNever(plotkey, 'Unimplemented plot key');
      return '';
  }
}

/**
 * Renders the legend (also toggle button action)
 */
@observer
class PlotKeyLegend0 extends React.Component<PlotKeyLegendProps> {
  /** Renders the component */
  /// FF2-436 Add data-tour attribute for walkthrough
  render() {
    return (
      <div data-tour={'plot-key-legend'} className={styles.plotKeyLegendContainer}>
        <div className={styles.desc}>{i18next.t('legend.showing', 'Showing:') + ' '}</div>
        <div className={styles.legendLabelContainer}>{this.renderLegendLabels()}</div>
      </div>
    );
  }

  renderLegendLabels(): JSX.Element[] {
    const { displayHeadlineRatio, displayHeadlineText, qr, t } = this.props;
    return qr.getPlotKeys().map(plotkey => (
      <div key={plotkey} className={styles.plotKeyLegend}>
        <PlotKeyLabel
          plotkey={plotkey}
          title={plotToLegendName(qr, t, plotkey, displayHeadlineRatio || !!displayHeadlineText)}
          plotKeyToggleState={this.props.plotKeyToggleState}
        />
      </div>
    ));
  }
}

export const PlotKeyLegend = withI18n(PlotKeyLegend0);



// WEBPACK FOOTER //
// ./src/ui/components/plot-key-legend/plot-key-legend.tsx